import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr, downloadXlsx } from "@/lib/api";
import { fmtEuroCents } from "@/lib/format";
import { toast } from "@/components/ui/sonner";
import { Plus, Save, Trash2, KeyRound, Truck, Upload, FileSpreadsheet, X } from "lucide-react";

const EMPTY = { name: "", contact: "", email: "", phone: "", category: "", notes: "", active: true };

export default function Vendors() {
  const qc = useQueryClient();
  const [edit, setEdit] = useState(null);
  const [account, setAccount] = useState(null);
  const [creds, setCreds] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["vendors"],
    queryFn: async () => (await api.get("/procurement/vendors")).data,
  });

  const save = useMutation({
    mutationFn: async (v) => {
      const body = { name: v.name, contact: v.contact || "", email: v.email || "", phone: v.phone || "",
        category: v.category || "", notes: v.notes || "", active: v.active !== false };
      return v.id ? (await api.put(`/procurement/vendors/${v.id}`, body)).data
        : (await api.post("/procurement/vendors", body)).data;
    },
    onSuccess: () => { setEdit(null); qc.invalidateQueries({ queryKey: ["vendors"] }); toast.success("Ο προμηθευτής αποθηκεύτηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/procurement/vendors/${id}`)).data,
    onSuccess: () => { setEdit(null); qc.invalidateQueries({ queryKey: ["vendors"] }); toast.success("Διαγράφηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const makeAccount = useMutation({
    mutationFn: async ({ id, username, password }) => (await api.post(`/procurement/vendors/${id}/account`, { username, password })).data,
    onSuccess: (d) => { setAccount(null); setCreds(d); qc.invalidateQueries({ queryKey: ["vendors"] }); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const importPrices = useMutation({
    mutationFn: async ({ id, file }) => {
      const fd = new FormData();
      fd.append("file", file);
      return (await api.post(`/procurement/prices/import?vendor_id=${id}`, fd)).data;
    },
    onSuccess: (d) => { qc.invalidateQueries({ queryKey: ["vendors"] }); qc.invalidateQueries({ queryKey: ["compare"] }); toast.success(`Αποθηκεύτηκαν ${d.saved} τιμές (${d.skipped} κενές)`); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const field = "mt-1 w-full rounded-xl border border-[#E3DED4] px-3 py-2 text-sm outline-none focus:border-[#45423C]";

  return (
    <div className="space-y-6" data-testid="vendors-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Προμήθειες</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1">Προμηθευτές</h1>
          <p className="text-[#6F6A60] text-sm mt-1">Στοιχεία, τιμοκατάλογοι και λογαριασμοί portal για να ανεβάζουν μόνοι τους τις τιμές.</p>
        </div>
        <button data-testid="vendor-new" onClick={() => setEdit({ ...EMPTY })}
          className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-4 py-2.5 text-sm font-semibold hover:opacity-90">
          <Plus className="h-4 w-4" /> Νέος προμηθευτής
        </button>
      </div>

      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E3DED4] flex items-center gap-2">
          <Truck className="h-4 w-4 text-[#C86235]" />
          <h2 className="font-medium text-[#45423C]">{data?.count || 0} προμηθευτές</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#45423C] text-left text-[10px] uppercase tracking-wider text-white/80">
                <th className="px-4 py-3">Επωνυμία</th>
                <th className="px-3 py-3">Επικοινωνία</th>
                <th className="px-3 py-3 text-center">Τιμές</th>
                <th className="px-3 py-3 text-center">Ανοιχτές παραγγελίες</th>
                <th className="px-3 py-3">Portal</th>
                <th className="px-3 py-3 text-right">Τιμοκατάλογος</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={6} className="px-4 py-8 text-center text-[#6F6A60]">Φόρτωση…</td></tr>}
              {(data?.rows || []).map((v) => (
                <tr key={v.id} data-testid={`vendor-row-${v.id}`} className="border-t border-[#E3DED4] hover:bg-[#F7F5F1]">
                  <td className="px-4 py-2.5 font-medium text-[#45423C]">
                    <button data-testid={`vendor-edit-${v.id}`} onClick={() => setEdit({ ...v })} className="hover:underline">{v.name}</button>
                    {v.category && <span className="ml-2 text-[10px] uppercase tracking-wider text-[#6F6A60]">{v.category}</span>}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-[#6F6A60]">{[v.contact, v.phone, v.email].filter(Boolean).join(" · ") || "—"}</td>
                  <td className="px-3 py-2.5 text-center tabular">{v.price_count || 0}</td>
                  <td className="px-3 py-2.5 text-center tabular">{v.open_orders || 0}</td>
                  <td className="px-3 py-2.5 text-xs">
                    {v.username
                      ? <span className="inline-flex items-center gap-1.5 text-[#6E7A63] font-semibold">{v.username}
                        <button data-testid={`vendor-reset-${v.id}`} onClick={() => setAccount({ id: v.id, name: v.name, username: v.username, password: "" })}
                          className="text-[#6F6A60] hover:underline">(νέος κωδικός)</button></span>
                      : <button data-testid={`vendor-account-${v.id}`} onClick={() => setAccount({ id: v.id, name: v.name, username: "", password: "" })}
                        className="inline-flex items-center gap-1.5 text-[#C86235] font-semibold hover:underline">
                        <KeyRound className="h-3.5 w-3.5" /> Δημιουργία λογαριασμού</button>}
                  </td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    <button data-testid={`vendor-template-${v.id}`} onClick={() => downloadXlsx(`/procurement/prices/template.xlsx?vendor_id=${v.id}`, `Timokatalogos_${v.name}.xlsx`)}
                      className="p-1.5 rounded-lg text-[#6F6A60] hover:bg-[#F1EDE6]" title="Πρότυπο τιμοκαταλόγου">
                      <FileSpreadsheet className="h-4 w-4" />
                    </button>
                    <label className="p-1.5 rounded-lg text-[#6F6A60] hover:bg-[#F1EDE6] inline-flex cursor-pointer" title="Ανέβασμα τιμών από Excel">
                      <Upload className="h-4 w-4" />
                      <input type="file" accept=".xlsx" className="hidden" data-testid={`vendor-import-${v.id}`}
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) importPrices.mutate({ id: v.id, file: f }); e.target.value = ""; }} />
                    </label>
                  </td>
                </tr>
              ))}
              {!isLoading && (data?.rows || []).length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-[#6F6A60]">Δεν υπάρχουν προμηθευτές ακόμη.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {edit && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" data-testid="vendor-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg mt-10 border border-[#E3DED4]">
            <div className="px-5 py-4 border-b border-[#E3DED4] flex items-center justify-between">
              <h3 className="font-medium text-[#45423C]">{edit.id ? "Επεξεργασία προμηθευτή" : "Νέος προμηθευτής"}</h3>
              <button data-testid="vendor-close" onClick={() => setEdit(null)} className="p-1.5 rounded-lg hover:bg-[#F1EDE6]"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-5 space-y-3">
              {[["name", "Επωνυμία"], ["contact", "Υπεύθυνος επικοινωνίας"], ["phone", "Τηλέφωνο"], ["email", "Email"], ["category", "Κατηγορία (π.χ. Κρεατικά)"]].map(([k, l]) => (
                <div key={k}>
                  <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">{l}</label>
                  <input data-testid={`vendor-${k}`} value={edit[k] || ""} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} className={field} />
                </div>
              ))}
              <div className="flex justify-between pt-2">
                {edit.id ? (
                  <button data-testid="vendor-delete" onClick={() => { if (window.confirm(`Διαγραφή του «${edit.name}»;`)) remove.mutate(edit.id); }}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-[#E3DED4] px-4 py-2 text-sm font-semibold text-[#991B1B] hover:bg-[#FEF2F2]">
                    <Trash2 className="h-4 w-4" /> Διαγραφή
                  </button>
                ) : <span />}
                <button data-testid="vendor-save" onClick={() => save.mutate(edit)} disabled={!edit.name?.trim() || save.isPending}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-[#45423C] text-white px-4 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-40">
                  <Save className="h-4 w-4" /> Αποθήκευση
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {account && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4" data-testid="vendor-account-modal">
          <div className="bg-white rounded-2xl w-full max-w-md mt-16 border border-[#E3DED4]">
            <div className="px-5 py-4 border-b border-[#E3DED4]">
              <h3 className="font-medium text-[#45423C]">Λογαριασμός portal · {account.name}</h3>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">Όνομα χρήστη</label>
                <input data-testid="account-username" value={account.username} onChange={(e) => setAccount({ ...account, username: e.target.value })} className={field} placeholder="π.χ. zamagias" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">Κωδικός (κενό = αυτόματος)</label>
                <input data-testid="account-password" value={account.password} onChange={(e) => setAccount({ ...account, password: e.target.value })} className={field} />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button data-testid="account-cancel" onClick={() => setAccount(null)} className="rounded-xl border border-[#E3DED4] px-4 py-2 text-sm font-semibold">Άκυρο</button>
                <button data-testid="account-create" onClick={() => makeAccount.mutate(account)} disabled={!account.username?.trim() || makeAccount.isPending}
                  className="rounded-xl bg-[#45423C] text-white px-4 py-2 text-sm font-semibold disabled:opacity-40">
                  {makeAccount.isPending ? "Δημιουργία…" : "Δημιουργία"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {creds && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4" data-testid="vendor-creds-modal">
          <div className="bg-white rounded-2xl w-full max-w-md mt-16 border border-[#E3DED4]">
            <div className="px-5 py-4 border-b border-[#E3DED4]"><h3 className="font-medium text-[#45423C]">Στοιχεία πρόσβασης</h3></div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-[#6F6A60]">Δώσε τα παρακάτω στον προμηθευτή. Ο κωδικός δεν εμφανίζεται ξανά.</p>
              <div className="rounded-xl bg-[#F7F5F1] p-4 font-mono text-sm space-y-1">
                <div data-testid="creds-username">Χρήστης: <strong>{creds.username}</strong></div>
                <div data-testid="creds-password">Κωδικός: <strong>{creds.password}</strong></div>
              </div>
              <button data-testid="creds-close" onClick={() => setCreds(null)} className="w-full rounded-xl bg-[#45423C] text-white px-4 py-2 text-sm font-semibold">Το αντέγραψα</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
